import { asList, humanizeKey } from '@/utils/diagnosisFindings'

/** The payload vocabulary this module knows. A higher version still renders via the fallbacks below. */
export const SOLUTION_CONTRACT_VERSION = 4

export const SOLUTION_TERMINAL_STATUSES = ['done', 'failed', 'nothing_to_repair']

const PAGE_STATUS_META = {
  ready: {
    label: 'ready',
    headline: 'Ready to paste',
    standfirst: 'The structured data below describes this page from what the page itself says.'
  },
  needs_review: {
    label: 'needs review',
    headline: 'Ready, with things to check',
    standfirst: 'The structured data below is complete. Some items want your eye before it goes live.'
  },
  needs_evidence: {
    label: 'needs a fact from you',
    headline: "Waiting on facts this page doesn't state",
    standfirst: "There's nothing safe to publish yet. Add the facts listed below to the page and run this again."
  },
  no_safe_fix: {
    label: 'no safe fix',
    headline: 'Nothing here can be fixed in your structured data',
    standfirst: "Every option would have claimed something the page doesn't support, so we wrote nothing."
  },
  delivery_prerequisite: {
    label: 'needs a developer',
    headline: 'A developer has to go first',
    standfirst: "What's wrong here is in how the page is served, not in its structured data. No block we could write would repair it."
  },
  stale_diagnosis: {
    label: 'scan out of date',
    headline: 'The page has changed since we last scanned it',
    standfirst: 'What we recorded is no longer what this page serves. Run the scan again first.'
  }
}

const ISSUE_OUTCOME_META = {
  repaired: { label: 'fixed', tone: 'positive' },
  needs_review: { label: 'worth a look', tone: 'warn' },
  needs_evidence: { label: 'needs a fact from you', tone: 'warn' },
  delivery_prerequisite: { label: 'needs a developer', tone: 'warn' },
  no_safe_fix: { label: 'no safe fix', tone: 'neutral' },
  stale_diagnosis: { label: 'scan out of date', tone: 'neutral' }
}

const ISSUE_PROBLEM_BY_CODE = {
  INVALID_MARKUP: "The structured data on this page was written in a way search engines can't read.",
  MISSING_SCHEMA: 'This page carried no structured data at all, so nothing told a search engine what it is about.',
  UNMODELED_PAGE_SUBJECT: 'What this page is actually about was never described in its structured data.',
  WRONG_ENTITY_TYPE: 'Your structured data described this page as the wrong kind of thing.',
  CONTENT_SCHEMA_CONFLICT: 'Your structured data says something the page itself contradicts.',
  UNSUPPORTED_SCHEMA_CLAIM: 'Your structured data claimed something the page never states.',
  TYPE_TOO_GENERIC: 'Your structured data used a description so broad it says almost nothing about this page.',
  MISSING_RELEVANT_PROPERTY: 'Your structured data left out details this page plainly states.',
  MISSING_ENTITY_RELATIONSHIP: 'Nothing in your structured data connects this to the other things it belongs with.',
  NO_STABLE_IDENTITY: 'Nothing in your structured data gives this an identifier that stays put.',
  JS_INVISIBLE: 'The structured data is added by a script, so a reader that does not run scripts never sees it.',
  INCONSISTENT_ENTITY_URL: 'Your page disagrees with itself about its own web address.',
  DUPLICATE_STRUCTURED_DATA: 'The same structured data was served more than once on this page.'
}

/**
 * What was done about a defect the agent repairs deterministically. The code is stored and the
 * sentence is not, so this wording stays editable after the row is written.
 *
 * These read alongside ISSUE_PROBLEM_BY_CODE, which is printed directly above them, so they say
 * what happened rather than restating what was wrong.
 *
 * `lead` is the clause the reader sees in bold, `body` the sentence or two under it. Every entry
 * takes the count, even the nine that ignore it, so the caller never tests whether one is callable.
 */
const NOTE_COPY = {
  duplicate_blocks_removed: (n) => ({
    lead: n
      ? `We removed ${n} duplicate ${pluralize(n, 'copy', 'copies')} of your structured data.`
      : 'We removed the duplicate copies of your structured data.',
    body: 'The same description was served more than once, which leaves a reader guessing which one counts.'
  }),
  no_duplicate_blocks: () => ({ lead: 'We checked for repeated blocks and found none.', body: '' }),
  retired_terms_replaced: (n) => ({
    lead: n
      ? `We replaced ${n} ${pluralize(n, 'term', 'terms')} schema.org no longer supports.`
      : 'We replaced the terms schema.org no longer supports.',
    body: 'Retired terms are quietly ignored, so anything they described was going unread.'
  }),
  no_retired_terms: () => ({ lead: 'Every schema.org term on this page is still supported.', body: '' }),
  repair_withheld: () => ({
    lead: "We have a fix for this, but it's held back for you to approve.",
    body: 'It asserts something we would rather you confirmed first.'
  }),
  no_remedy_reached: () => ({
    lead: "We couldn't describe this page in a way we were confident was accurate.",
    body: "Rather than guess, we've left it alone."
  }),
  address_corrected: () => ({
    lead: "Your page's structured data listed the wrong web address.",
    body: "Search engines and AI assistants use that address to tell which page they're reading — when it points somewhere else, they can credit the wrong page, or none at all. We've corrected it to the address this page actually loads at."
  }),
  address_conflict: () => ({
    lead: 'This page tells search engines its official address is a different page.',
    body: "It loads at one address but declares another, so crawlers may index the wrong one. This isn't something we can fix in the structured data — it needs changing in your site's template or redirect rules. The three addresses we found are below."
  }),
  address_already_agrees: () => ({
    lead: 'Already sorted.',
    body: "This address was wrong when we last scanned your site, and it's right now — so there's nothing to change here."
  }),
  no_settled_address: () => ({
    lead: "We couldn't tell which address is the official one for this page.",
    body: "So we've left your structured data alone rather than guess at it."
  }),
  js_invisible: () => ({
    lead: 'Serve this description in the page itself, rather than adding it with a script.',
    body: 'A reader that does not run scripts never sees anything a script adds.'
  })
}

/** For issues carrying no note code we have wording for. issueProblemFor still says what was wrong. */
const OUTCOME_FALLBACK_COPY = {
  repaired: { lead: 'We fixed this for you.', body: 'The block below is the corrected version.' },
  needs_review: { lead: 'This one is worth your eye before it goes live.', body: 'We have written a fix, but it leans on a reading of your page we would rather you confirmed.' },
  needs_evidence: { lead: 'We need one fact from you before we can answer this.', body: 'Your page does not state it plainly enough for us to assert it on your behalf.' },
  delivery_prerequisite: { lead: 'This one is for your developer, not for us.', body: 'What is wrong is in how the page is served, so no change to your structured data would repair it.' },
  no_safe_fix: { lead: 'We left this alone on purpose.', body: 'Every option would have claimed something your page does not actually say.' },
  stale_diagnosis: { lead: 'Your page has changed since we last looked.', body: 'What we recorded is no longer what this page serves, so re-run the scan before acting on it.' }
}

/** Strongest first. declared_by_the_page is settled by the fetch, so it must never read as inferred. */
const SOURCE_LEVEL_META = {
  declared_by_the_page: { label: 'declared by the page itself', tone: 'quoted' },
  stated_on_page: { label: 'stated on this page', tone: 'quoted' },
  stated_on_site: { label: 'stated on your site', tone: 'quoted' },
  entailed: { label: 'inferred from wording', tone: 'inferred' }
}

const SOURCE_FOUND_IN_COPY = {
  text: 'your page text',
  title: 'your page title',
  headings: 'a heading on your page',
  footer: 'your page footer'
}

const FLAGGED_CONCERN_COPY = {
  wrong_subject: 'possibly the wrong subject',
  unsupported_claim: 'possibly unsupported by the page',
  entity_conflation: 'two things possibly merged into one',
  overstated: 'possibly overstated'
}

const CHANGE_KIND_META = {
  added: { label: 'added', tone: 'positive' },
  removed: { label: 'removed', tone: 'negative' },
  modified: { label: 'changed', tone: 'warn' }
}

const SEVERITY_LABELS = { high: 'High severity', medium: 'Medium severity', low: 'Low severity' }

const SCHEMA_TYPE_NOUNS = {
  AnatomicalSystem: 'anatomical system',
  Answer: 'answer',
  Article: 'article',
  BreadcrumbList: 'breadcrumb trail',
  Certification: 'certification',
  City: 'city',
  CollegeOrUniversity: 'school',
  EducationalOccupationalProgram: 'program',
  DiagnosticProcedure: 'procedure',
  FAQPage: 'FAQ page',
  Hospital: 'hospital',
  ImageObject: 'image',
  IndividualPhysician: 'physician',
  MedicalClinic: 'clinic',
  MedicalCondition: 'condition',
  MedicalDevice: 'device',
  MedicalOrganization: 'medical organization',
  MedicalProcedure: 'procedure',
  MedicalRiskFactor: 'risk factor',
  MedicalStudy: 'study',
  MedicalTest: 'test',
  MedicalTherapy: 'treatment',
  MedicalTrial: 'clinical trial',
  MedicalWebPage: 'page',
  NewsArticle: 'article',
  Organization: 'organization',
  Person: 'person',
  Physician: 'physician',
  PostalAddress: 'postal address',
  Question: 'question',
  RadiationTherapy: 'treatment',
  WebPage: 'page',
  WebSite: 'site'
}

/** Fuller phrases than SCHEMA_TYPE_NOUNS, which is tuned for mid-sentence use where "page" reads right. */
const NODE_ORIENTATION_NOUNS = {
  BreadcrumbList: 'the trail of links at the top of your page',
  CollegeOrUniversity: 'your school',
  EducationalOccupationalProgram: 'this program',
  Hospital: 'your hospital',
  IndividualPhysician: 'the physician this page is about',
  MedicalClinic: 'your clinic',
  MedicalOrganization: 'your organization',
  MedicalWebPage: "your page's main listing",
  Organization: 'your organization',
  Person: 'the person this page is about',
  Physician: 'the physician this page is about',
  PostalAddress: 'your postal address',
  WebPage: "your page's main listing",
  WebSite: 'your website as a whole'
}

/** Plain nouns for schema.org properties, so a change headline reads as a sentence. */
const PROPERTY_NOUNS = {
  '@id': 'the permanent identifier',
  '@type': 'the kind of thing this is',
  address: 'the postal address',
  areaServed: 'the area you serve',
  dateModified: 'the date it was last updated',
  datePublished: 'the publication date',
  description: 'the description',
  email: 'the email address',
  image: 'the image',
  inLanguage: 'the language',
  isBasedOn: 'what it is based on',
  isPartOf: 'what it is part of',
  logo: 'the logo',
  medicalSpecialty: 'the specialty',
  name: 'the name',
  openingHours: 'the opening hours',
  openingHoursSpecification: 'the opening hours',
  parentOrganization: 'the parent organization',
  sameAs: 'the links to your other profiles',
  telephone: 'the phone number',
  url: 'the web address'
}

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

function key(value) {
  return String(value == null ? '' : value).trim()
}

function lowerKey(value) {
  return key(value).toLowerCase()
}

function capitalize(value) {
  const text = key(value)
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''
}

function countWord(count) {
  const n = Number(count)
  return Number.isInteger(n) && n >= 0 && n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : String(count)
}

const HTML_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' }

/** Names are copied verbatim from the page's markup, and some pages escape them twice. */
function decodeEntities(text) {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity) => {
    if (entity[0] !== '#') return HTML_ENTITIES[entity.toLowerCase()] ?? match
    const code = entity[1].toLowerCase() === 'x' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10)
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match
  })
}

function nameOf(node) {
  return decodeEntities(key(node && node.name))
}

function withArticle(phrase) {
  if (!phrase) return ''
  return `${/^[aeiou]/i.test(phrase) ? 'an' : 'a'} ${phrase}`
}

function typeNounsFor(types) {
  return listPhrase(asList(types).map((type) => schemaTypeNoun(type)).filter(Boolean))
}

/** "a, b and c", for nodes that carry more than one type. */
function listPhrase(items) {
  if (items.length <= 1) return items[0] || ''
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

function pluralize(count, singular, plural) {
  return Number(count) === 1 ? singular : plural
}

/** A count worth printing, or null. Only two of the eleven note codes carry a count at all. */
function positiveCount(value) {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : null
}

/** Plain-English noun for a schema.org type, so change copy reads as a sentence. */
export function schemaTypeNoun(type) {
  const raw = key(type)
  if (!raw) return ''
  if (SCHEMA_TYPE_NOUNS[raw]) return SCHEMA_TYPE_NOUNS[raw]
  return humanizeKey(raw.replace(/([a-z0-9])([A-Z])/g, '$1 $2')).toLowerCase()
}

/** Plain noun for a schema.org property. Falls back to the property name made readable. */
export function propertyNounFor(field) {
  const raw = key(field)
  if (!raw) return ''
  if (PROPERTY_NOUNS[raw]) return PROPERTY_NOUNS[raw]

  const words = humanizeKey(raw.replace(/^@/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2')).toLowerCase()
  // Boolean-ish properties read as verbs, and "the is accepting new patients" is not English.
  if (/^(is|has|are|can|does|was|were|should|will|accepts|allows|requires)\b/.test(words)) {
    return `whether it ${words}`
  }
  return `the ${words}`
}

/** The "on ..." line under a change, in parts so the noun can be bold and the name quoted. */
export function nodeOrientationFor(node) {
  const types = nodeTypesFor(node)
  // Several types are possible; schema.org puts the primary one first, so first match wins.
  const named = types.find((type) => NODE_ORIENTATION_NOUNS[type])
  if (named) return { noun: NODE_ORIENTATION_NOUNS[named], name: nameOf(node) }

  const generic = typeNounsFor(types)
  return {
    noun: generic ? `the ${generic} on this page` : 'this page',
    name: nameOf(node)
  }
}

/** Stored rows keep the version they were written with; rows from before the key are version 1. */
export function contractVersionFor(view) {
  return Number(view && view.contract_version) || 1
}

/** `types` is always a list on contract 3. Bare strings are pre-v3 rows still in the database. */
export function nodeTypesFor(node) {
  const raw = node && (node.types == null ? node.type : node.types)
  return asList(raw).map((type) => key(type)).filter(Boolean)
}

/** What the change is about. remove_block names no node, so it gets its own phrasing. */
export function changeOrientationFor(change) {
  if (lowerKey(change && change.operation) === 'remove_block') {
    return { noun: 'the structured data already on your page', name: '' }
  }
  return nodeOrientationFor(change && change.node)
}

export function severityLabelFor(severity) {
  return SEVERITY_LABELS[lowerKey(severity)] || `${capitalize(humanizeKey(lowerKey(severity))) || 'Unrated'} severity`
}

export const LOCKED_PAGE_STANDFIRST = 'Upgrade to see the structured data for this page.'

/** A locked page keeps its status and headline, but has no structured data below to describe. */
export function pageStatusMetaFor(status, { locked = false } = {}) {
  const k = lowerKey(status)
  const meta =
    PAGE_STATUS_META[k] || {
      label: humanizeKey(k) || 'unknown',
      headline: 'This page was read',
      standfirst: 'The agent recorded a status this dashboard does not have wording for yet.'
    }
  return locked ? { ...meta, standfirst: LOCKED_PAGE_STANDFIRST } : meta
}

export function issueProblemFor(code) {
  const k = key(code).toUpperCase()
  return ISSUE_PROBLEM_BY_CODE[k] || "We found something on this page we don't have plain wording for yet."
}

export function issueOutcomeMetaFor(outcome) {
  const k = lowerKey(outcome)
  return ISSUE_OUTCOME_META[k] || { label: humanizeKey(k) || 'recorded', tone: 'neutral' }
}

/** Our sentence on top, the agent's engineer-to-engineer prose demoted to `supporting`. */
export function issueCardCopyFor(issue) {
  const note = NOTE_COPY[lowerKey(issue && issue.note)]
  const copy = note
    ? note(positiveCount(issue && issue.count))
    : (OUTCOME_FALLBACK_COPY[lowerKey(issue && issue.outcome)] || { lead: 'We recorded this on your page.', body: '' })

  return {
    lead: copy.lead,
    body: copy.body,
    supporting: key(issue && issue.detail)
  }
}

// An unrecognised level falls back to the inference treatment: we cannot vouch that
// an unknown provenance is a direct transcription, and overclaiming is the worse error.
export function sourceLevelMetaFor(level) {
  const k = lowerKey(level)
  return SOURCE_LEVEL_META[k] || { label: humanizeKey(k) || 'read from your page', tone: 'inferred' }
}

/**
 * Where a value came from, for the rows with no quote to carry the blockquote's own pill.
 * Empty when nothing was recorded, so the row drops the line rather than announcing an absence.
 */
export function valueProvenanceFor(source) {
  if (!source) return ''

  const level = lowerKey(source.level)
  const foundIn = key(source.found_in) ? sourceFoundInFor(source.found_in) : ''

  if (level === 'declared_by_the_page') {
    return 'Taken from the page itself. This is the address it loads at and declares as official — read directly, not inferred from anything written on the page.'
  }
  if (level === 'entailed') {
    return foundIn ? `Inferred from the wording in ${foundIn}.` : 'Inferred from the wording on your page.'
  }
  if (level === 'stated_on_site') return 'Taken from what your site says elsewhere.'
  if (level === 'stated_on_page') {
    return foundIn ? `Taken from ${foundIn}.` : 'Taken from what this page says.'
  }
  return foundIn ? `Read from ${foundIn}.` : 'Read from your page.'
}

export function sourceFoundInFor(foundIn) {
  const k = lowerKey(foundIn)
  return SOURCE_FOUND_IN_COPY[k] || (k ? `your page ${humanizeKey(k)}` : 'your page')
}

export function flaggedConcernFor(concern) {
  const k = lowerKey(concern)
  return FLAGGED_CONCERN_COPY[k] || humanizeKey(k) || 'worth a second look'
}

export function changeKindMetaFor(kind) {
  const k = lowerKey(kind)
  return CHANGE_KIND_META[k] || { label: humanizeKey(k) || 'changed', tone: 'neutral' }
}

/** The change's sentence, composed from operation + node + field. */
export function changeSummaryFor(change, version = SOLUTION_CONTRACT_VERSION) {
  const operation = lowerKey(change && change.operation)
  const node = (change && change.node) || {}
  const name = nameOf(node)
  const noun = propertyNounFor(change && change.field)
  const typeNoun = schemaTypeNoun(nodeTypesFor(node)[0])

  switch (operation) {
    case 'add_node':
      if (name) return `Described ${name}`
      // A node added to carry one property names the same thing twice: "the postal address to
      // the postal address".
      if (noun && typeNoun && !noun.includes(typeNoun)) return `Added ${noun} to the ${typeNoun}`
      if (typeNoun) return `Described the ${typeNoun}`
      if (noun) return `Added ${noun}`
      return 'Described something the page was missing'
    case 'add_block':
      if (name) return `Described ${name}`
      return 'Added the page-level wrapper the rest hangs from'
    case 'remove_block':
      return 'Removed a duplicate copy of your structured data'
    case 'add_property':
      return noun ? `Added ${noun}` : 'Added a detail'
    case 'replace_property':
      return noun ? `Updated ${noun}` : 'Updated a detail'
    case 'remove_property':
      return noun ? `Removed ${noun}` : 'Removed a detail'
    case 'move_property':
      return moveSummaryFor(change, version)
    case 'set_type':
      return setTypeSummaryFor(change, version)
    case 'set_id':
      return name ? `Gave ${name} a permanent identifier` : 'Gave this a permanent identifier'
    default:
      return 'Updated your structured data'
  }
}

/**
 * `node` is the node before the change, so on v3 it holds the type being replaced and cannot
 * describe the outcome. v4 states the outcome in `becomes`; v2 and earlier put the first
 * resulting type in `node.type`, which is right for single-type retypes only.
 */
function setTypeSummaryFor(change, version) {
  const fallback = 'Corrected what kind of thing this is'
  const node = change && change.node

  if (version <= 2) {
    const first = typeNounsFor(nodeTypesFor(node).slice(0, 1))
    return first ? `Described this as ${withArticle(first)}` : fallback
  }

  const becomes = asList(change && change.becomes).map((type) => key(type)).filter(Boolean)
  if (version < 4 || !becomes.length) return fallback

  const was = nodeTypesFor(node)
  const added = becomes.filter((type) => !was.includes(type))
  const dropped = was.filter((type) => !becomes.includes(type))

  if (!added.length && !dropped.length) return fallback
  if (!was.length) return `Described this as ${withArticle(typeNounsFor(becomes))}`
  if (!dropped.length) return `Also described this as ${withArticle(typeNounsFor(added))}`
  if (!added.length) {
    return `Now described only as ${withArticle(typeNounsFor(becomes))}, not ${withArticle(typeNounsFor(dropped))}`
  }
  return `Changed this from ${withArticle(typeNounsFor(was))} to ${withArticle(typeNounsFor(becomes))}`
}

/** `moved` arrives on v4; earlier rows name only the property, never where it went. */
function moveSummaryFor(change, version) {
  const noun = propertyNounFor(change && change.field) || 'a detail'
  const fallback = `Moved ${noun} to a better-suited part of your structured data`
  const moved = version >= 4 && change && change.moved
  if (!moved) return fallback

  const from = change.node
  const to = moved.to_node
  if (key(moved.renamed_to) && isSameNode(from, to)) return `Updated ${noun} to the name schema.org uses now`
  if (!nameOf(to) && !nodeTypesFor(to).length) return fallback
  return `Moved ${noun} from ${nodeOrientationFor(from).noun} to ${nodeOrientationFor(to).noun}`
}

function isSameNode(a, b) {
  return nameOf(a) === nameOf(b) && nodeTypesFor(a).join() === nodeTypesFor(b).join()
}

/**
 * Stands in for the quote list when a change asserts no literal value. Keyed on the
 * operation, because "adds structure" is wrong for a removal or a retyping.
 */
export function changeNoValuesLineFor(change) {
  const operation = lowerKey(change && change.operation)
  if (operation === 'remove_block' || operation === 'remove_node' || operation === 'remove_property') {
    return 'This one takes structured data away rather than claiming anything, so there is nothing quoted against it.'
  }
  if (operation === 'set_type' || operation === 'set_id') {
    return 'This one relabels what is already there rather than asserting a new fact, so there is nothing quoted against it.'
  }
  if (operation === 'move_property') {
    return 'This one moves a detail you already had somewhere it reads correctly, so there is nothing quoted against it.'
  }
  return 'This one adds structure rather than facts, so there is nothing quoted against it.'
}

export function solutionPagePath(url) {
  const raw = key(url)
  if (!raw) return ''
  try {
    const parsed = new URL(raw)
    return `${parsed.pathname}${parsed.search}` || '/'
  } catch {
    return raw
  }
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatCheckedAt(value) {
  const raw = key(value)
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  const time = `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
  return `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${time} UTC`
}

/** What the agent says before it starts. */
export function agentIntroLineFor(pageCount) {
  const pages = Number(pageCount) || 0
  let subject = 'the pages'
  if (pages === 1) subject = 'the page'
  else if (pages === 2) subject = 'both pages'
  else if (pages > 2) subject = `all ${countWord(pages)} pages`
  return `I'm writing the structured data for ${subject} this finding names. I'll read each live page first so every value I assert is something your own site already says.`
}

/** How to deploy the block. On `replace` the reader must be told to remove what is there. */
export function installNoteFor(install) {
  const action = lowerKey(install && install.action)
  const where = key(install && install.where) || 'head'
  const scripts = Number(install && install.scripts_on_page) || 0

  if (action === 'add') {
    return `Your page carries no structured data today. Paste this into the <${where}> section.`
  }
  if (action === 'replace') {
    const count = scripts > 0
      ? `${scripts} structured-data ${pluralize(scripts, 'script', 'scripts')}`
      : 'structured data'
    const remove = scripts === 1 ? 'Remove it and paste this one in its place' : 'Remove them all and paste this one in their place'
    return `Your page serves ${count}. ${remove} — adding it alongside would describe the same thing twice.`
  }
  return `Paste this into the <${where}> section of your page.`
}

/** The scripts a `replace` asks the reader to delete, each named by what it carries. */
export function installReplacementsFor(install) {
  return asList(install && install.replace).map((entry, index) => ({
    position: Number(entry && entry.position) || index + 1,
    type: key(entry && entry.type),
    label: key(entry && entry.type)
      ? `Script ${Number(entry && entry.position) || index + 1} — ${key(entry.type)}`
      : `Script ${Number(entry && entry.position) || index + 1}`
  }))
}

/**
 * One line list for the whole page: `before` removals interleaved with `code`.
 * Returns null when the two documents do not reconcile, so the caller can fall
 * back to rendering `code` plainly rather than showing a wrong diff.
 */
export function buildMarkupLines(markup) {
  if (!markup || typeof markup !== 'object') return null

  const code = typeof markup.code === 'string' ? markup.code : ''
  const before = typeof markup.before === 'string' ? markup.before : ''
  const codeLines = code === '' ? [] : code.split('\n')
  const beforeLines = before === '' ? [] : before.split('\n')

  const added = {}
  const removed = {}
  asList(markup.spans).forEach((span) => {
    if (!span || typeof span !== 'object') return
    const from = Number(span.from)
    const to = Number(span.to)
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from) return
    const onBefore = lowerKey(span.side) === 'before'
    const target = onBefore ? removed : added
    const limit = onBefore ? beforeLines.length : codeLines.length
    for (let n = from; n <= to && n <= limit; n += 1) target[n] = key(span.change) || null
  })

  const lines = []
  let beforeCursor = 1
  let codeCursor = 1
  let displayNo = 0

  while (codeCursor <= codeLines.length || beforeCursor <= beforeLines.length) {
    if (beforeCursor <= beforeLines.length && Object.prototype.hasOwnProperty.call(removed, beforeCursor)) {
      displayNo += 1
      lines.push({ n: displayNo, kind: 'removed', change: removed[beforeCursor], text: beforeLines[beforeCursor - 1] })
      beforeCursor += 1
      continue
    }
    if (codeCursor > codeLines.length) break

    displayNo += 1
    if (Object.prototype.hasOwnProperty.call(added, codeCursor)) {
      lines.push({ n: displayNo, kind: 'added', change: added[codeCursor], text: codeLines[codeCursor - 1] })
    } else {
      lines.push({ n: displayNo, kind: 'unchanged', change: null, text: codeLines[codeCursor - 1] })
      beforeCursor += 1
    }
    codeCursor += 1
  }

  const addedCount = Object.keys(added).length
  const removedCount = Object.keys(removed).length
  const unchangedCount = lines.filter((line) => line.kind === 'unchanged').length

  const reconciles =
    unchangedCount === codeLines.length - addedCount && unchangedCount === beforeLines.length - removedCount
  if (!reconciles) return null

  return { lines, addedCount, removedCount, unchangedCount }
}

/** Plain fallback when the spans do not reconcile: the block, no gutter, nothing implied. */
export function plainMarkupLines(markup) {
  const code = typeof (markup && markup.code) === 'string' ? markup.code : ''
  if (code === '') return []
  return code.split('\n').map((text, index) => ({ n: index + 1, kind: 'plain', change: null, text }))
}

export function changeLineCountFor(changeId, lines) {
  const id = key(changeId)
  if (!id) return 0
  return asList(lines).filter((line) => key(line && line.change) === id).length
}

export function changeLineHintFor(changeId, lines) {
  const count = changeLineCountFor(changeId, lines)
  if (count === 0) return 'not shown in the block below'
  if (count === 1) return '1 line in the block below'
  return `${count} lines in the block below`
}

const SEVERITY_RANK = { high: 0, medium: 1, low: 2 }

/** Worst first, ties keeping the agent's order. The page-level severity is only the worst of these. */
export function sortIssuesBySeverity(issues) {
  return asList(issues)
    .map((issue, index) => ({ issue, index }))
    .sort((a, b) => {
      const rank = severityRankFor(a.issue) - severityRankFor(b.issue)
      return rank === 0 ? a.index - b.index : rank
    })
    .map((entry) => entry.issue)
}

function severityRankFor(issue) {
  const rank = SEVERITY_RANK[lowerKey(issue && issue.severity)]
  return rank === undefined ? SEVERITY_RANK.low + 1 : rank
}

/** The three addresses, but only when they disagree. A repaired page ships all three identical. */
export function issueAddressRowsFor(addresses) {
  if (!addresses) return null

  const rows = [
    { label: 'Loads at', value: key(addresses.loads_at) },
    { label: 'Canonical says', value: key(addresses.canonical) },
    { label: 'Your sharing tag says', value: key(addresses.og_url) }
  ].filter((row) => row.value)

  return new Set(rows.map((row) => row.value)).size > 1 ? rows : null
}

/** The comfort beat: what else moved, answered before the reader asks. */
export function untouchedSummaryFor(count) {
  const n = Number(count) || 0
  if (!n) return ''
  return `We left ${countWord(n)} ${pluralize(n, 'thing', 'things')} on this page exactly as ${pluralize(n, 'it was', 'they were')}.`
}

/** One carried-through item. Never stringifies an unknown shape: raw JSON on a reassurance card jars. */
export function untouchedItemFor(item) {
  if (typeof item === 'string') return untouchedFromLabel(item)

  const node = (item && item.node) || item || {}
  return untouchedFromParts(nameOf(node), nodeTypesFor(node))
}

/**
 * Pre-v3 rows shipped display labels — "MedicalOrganization — Hoag Family Cancer Institute",
 * "Organization, Person — Home | …", or a lone "BreadcrumbList". Those runs are still openable,
 * and each label leads with a type name that must not reach the card.
 */
function untouchedFromLabel(label) {
  const text = key(label)
  if (!text) return untouchedFromParts('', [])

  const [, rawTypes, rawName] = /^(.*?)\s+—\s+(.*)$/.exec(text) || []
  if (rawTypes && rawName) {
    return untouchedFromParts(decodeEntities(key(rawName)), rawTypes.split(',').map((part) => part.trim()))
  }
  return untouchedFromParts('', [text])
}

function untouchedFromParts(name, types) {
  const noun = typeNounsFor(types)

  // Only what the thing is: the standfirst already says they were left alone, and a page can
  // carry thirty of these.
  if (name) return { title: name, detail: capitalize(noun) }
  if (noun) return { title: capitalize(noun), detail: '' }
  return { title: 'Part of your existing structured data', detail: '' }
}

export function pageCountsFor(artifacts) {
  const list = asList(artifacts)
  let additions = 0
  let replacements = 0
  list.forEach((artifact) => {
    const action = lowerKey(artifact && artifact.client_view && artifact.client_view.install && artifact.client_view.install.action)
    if (action === 'replace') replacements += 1
    else if (action === 'add') additions += 1
  })
  const parts = []
  if (additions) parts.push(`${additions} ${pluralize(additions, 'addition', 'additions')}`)
  if (replacements) parts.push(`${replacements} ${pluralize(replacements, 'replacement', 'replacements')}`)
  return { additions, replacements, label: parts.join(' · ') }
}

/** A locked page arrives without its changes or markup, only how many changes it holds. */
export function isLockedArtifact(artifact) {
  return Boolean(artifact && artifact.locked)
}

export function artifactChangeCountFor(artifact) {
  if (isLockedArtifact(artifact)) return Number(artifact.locked_summary && artifact.locked_summary.change_count) || 0
  return asList(artifact && artifact.client_view && artifact.client_view.changes).length
}

export function artifactMetaFor(artifact, lines) {
  const view = (artifact && artifact.client_view) || {}
  const changeCount = artifactChangeCountFor(artifact)
  const changeLabel = `${changeCount} ${pluralize(changeCount, 'change', 'changes')}`
  // Line counts come from markup, which a locked page never receives.
  if (isLockedArtifact(artifact)) {
    return { changeCount, changeLabel, lineLabel: 'locked', meta: `${changeLabel} · locked` }
  }
  const counts = lines || buildMarkupLines(view.markup)
  const added = counts ? counts.addedCount : 0
  const removed = counts ? counts.removedCount : 0
  const lineLabel =
    added && removed
      ? `+${added} / −${removed} lines`
      : added
      ? `+${added} lines`
      : removed
      ? `−${removed} lines`
      : 'no line changes'
  return {
    changeCount,
    changeLabel,
    lineLabel,
    meta: `${changeLabel} · ${lineLabel}`
  }
}

/**
 * The paragraphs the agent "says" once the run is done. Composed here, never in Python
 * and never in a template. Claims clarity about the structured data, never an outcome.
 */
export function runNarrativeFor(run, artifacts) {
  const list = asList(artifacts)
  if (!list.length) return []

  const views = list.map((artifact) => (artifact && artifact.client_view) || {})
  const { additions, replacements } = pageCountsFor(list)
  // Quoted-versus-inferred values and the flagged/needs/prerequisite totals live in the
  // content a locked page withholds, so a run with one says nothing about them at all.
  const anyLocked = list.some(isLockedArtifact)

  let stated = 0
  let inferred = 0
  let totalChanges = 0
  let flaggedCount = 0
  let prerequisiteCount = 0
  let needsCount = 0

  views.forEach((view, index) => {
    totalChanges += artifactChangeCountFor(list[index])
    if (isLockedArtifact(list[index])) return
    const changes = asList(view.changes)
    changes.forEach((change) => {
      asList(change && change.values).forEach((value) => {
        const level = lowerKey(value && value.source && value.source.level)
        if (!level) return
        if (level === 'entailed') inferred += 1
        else stated += 1
      })
    })
    flaggedCount += asList(view.flagged).length
    needsCount += asList(view.needs_from_you).length
    prerequisiteCount += asList(view.issues).filter(
      (issue) => lowerKey(issue && issue.outcome) === 'delivery_prerequisite'
    ).length
  })

  const pages = list.length
  const sentences = [`Done — one block per page, ${countWord(pages)} in all.`]

  if (additions && replacements) {
    sentences.push(
      `${capitalize(countWord(additions))} ${pluralize(additions, 'page', 'pages')} carried no structured data at all, so ${pluralize(additions, 'that one is an addition', 'those are additions')}; ${countWord(replacements)} already ${pluralize(replacements, 'serves', 'serve')} structured data, so ${pluralize(replacements, 'that one replaces it', 'those replace it')}.`
    )
  } else if (replacements) {
    sentences.push('Each page already serves structured data, so every block replaces what is there.')
  } else if (additions) {
    sentences.push('None of them carried structured data, so every block is an addition.')
  }

  sentences.push(`${totalChanges} ${pluralize(totalChanges, 'change', 'changes')} in all.`)
  if (!anyLocked && (stated || inferred)) {
    sentences.push(
      inferred
        ? `${stated} ${pluralize(stated, 'value is', 'values are')} quoted from your own pages; ${inferred} ${pluralize(inferred, 'is', 'are')} inferred from their wording.`
        : 'Every value is quoted from your own pages.'
    )
  }

  const paragraphs = [sentences.join(' ')]

  const clauses = []
  if (flaggedCount) {
    clauses.push(
      `${countWord(flaggedCount)} ${pluralize(flaggedCount, 'item was', 'items were')} flagged on a second read`
    )
  }
  if (prerequisiteCount) {
    clauses.push(
      `${countWord(prerequisiteCount)} ${pluralize(prerequisiteCount, 'problem needs', 'problems need')} a developer rather than structured data`
    )
  }
  if (needsCount) {
    clauses.push(
      `${countWord(needsCount)} ${pluralize(needsCount, 'fact is', 'facts are')} named on your pages but not stated clearly enough to assert`
    )
  }
  if (!anyLocked && clauses.length) {
    const joined =
      clauses.length === 1
        ? clauses[0]
        : `${clauses.slice(0, -1).join(', ')} and ${clauses[clauses.length - 1]}`
    paragraphs.push(`Before you go through them: ${joined}.`)
  }

  return paragraphs
}

export function isTerminalRunStatus(status) {
  return SOLUTION_TERMINAL_STATUSES.includes(lowerKey(status))
}

/** Pages already written, newest last, so the reader watches them land one at a time. */
function writtenPageDetails(run, artifacts, pagesTotal) {
  const written = asList(run && run.artifacts)
    .map((artifact) => solutionPagePath(artifact && artifact.artifact_key))
    .filter(Boolean)
  const counted = pagesTotal ? [`${artifacts} of ${pagesTotal} pages written`] : []
  return counted.concat(written)
}

/**
 * The four loading steps, as markers only. They carry no durations on purpose: the row
 * timestamps record when the agent wrote to the database, not when a phase started or
 * finished, so the gap between two of them times an insert loop rather than the work the
 * label names. State comes from counts instead — snapshots and artifacts against pages_total.
 */
export function solutionRunStepsFor(run) {
  const status = lowerKey(run && run.status)
  const pagesTotal = Number(run && run.pages_total) || 0
  const snapshots = Number(run && run.snapshots_count) || 0
  const artifacts = Number(run && run.artifacts_count) || 0
  const terminal = isTerminalRunStatus(status)
  const failed = status === 'failed' || status === 'nothing_to_repair'

  const crawlDone = snapshots > 0
  const readDone = crawlDone && (pagesTotal ? snapshots >= pagesTotal : terminal)
  // Not "until the first artifact lands": every page is solved in that window, so waiting on it
  // parks the reader under this label for the whole generation and starts the next step after
  // a page is already written. Nothing distinct happens here, so it closes with the reading.
  const typesDone = readDone
  const writeDone = status === 'done'

  const stateFor = (done, previousDone) => {
    if (done) return 'done'
    if (failed) return 'pending'
    return previousDone ? 'active' : 'pending'
  }

  const site = key(run && run.client_view && run.client_view.site)

  return [
    {
      label: 'Reading your site',
      state: stateFor(crawlDone, status !== 'pending'),
      details: site ? [site] : []
    },
    {
      label: 'Checking the structured data on each page',
      state: stateFor(readDone, crawlDone),
      details:
        !readDone && crawlDone && pagesTotal ? [`${snapshots} of ${pagesTotal} pages read`] : []
    },
    {
      label: 'Reading schema.org for the types that fit',
      state: stateFor(typesDone, readDone),
      details: []
    },
    {
      label: 'Generating schema fixes',
      state: stateFor(writeDone, typesDone),
      details: writeDone || !typesDone ? [] : writtenPageDetails(run, artifacts, pagesTotal)
    }
  ]
}
